/**
 * Handing the till to whoever is working next.
 *
 * PINs could be set from the back office and read by nothing: `employee_by_pin`
 * had no route and no caller, so every PIN in the system was four digits
 * nobody could type anywhere. This is the missing half.
 *
 * WHY THIS IS NOT SIGNING OUT AND IN
 *
 * Signing the till out ends the session the whole terminal runs on, and signing
 * back in means an email and a twelve-character password typed on a tablet
 * between orders. Nobody does that eight times a shift; what they do instead is
 * leave the till signed in as whoever opened it, and then every void, every
 * discount and every tab is recorded against that person for the rest of the
 * night. A PIN is what makes the record true.
 *
 * WHY IT SITS BEHIND THE SITE BUTTON
 *
 * Next to "which site" and "sign out", because it is the same kind of thing —
 * a fact about this terminal rather than about the food. It is deliberately
 * not in the header: swapping user is a considered act, and a keypad one
 * mis-tap away from the floor plan would be opened by accident all evening.
 *
 * WHAT IT DOES NOT SOLVE, AND CANNOT
 *
 * Somebody who watches a manager type theirs gets a manager's session. Four
 * digits on a shared screen is a weak secret and always will be; the answer is
 * that PINs are personal, that guessing is rate-limited per restaurant, and
 * that what the swap buys is an honest name against each action rather than a
 * security boundary.
 */

import { useState } from 'react';

import { usePinSignIn } from '@restaurant/api-client';
import { ActionError, Button, NumberPad, Sheet } from '@restaurant/ui';

import { rememberPin } from './offline/pins';

const PIN_MAX = 8;

/**
 * The control in the terminal sheet, beside who is signed in: the button says
 * what it is for, so it needs no question of its own above it.
 */
export function PinSwap() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)}>Someone else? Use a PIN</Button>
      {open && <PinSheet onDismiss={() => setOpen(false)} />}
    </>
  );
}

function PinSheet({ onDismiss }: { onDismiss: () => void }) {
  const swap = usePinSignIn();
  const [pin, setPin] = useState('');

  // A successful swap drops every cached query, so this sheet is unmounted by
  // the shell before it could render anything — there is deliberately no
  // "done" state to fall through to.
  return (
    <Sheet title="Take over the till" onDismiss={onDismiss}>
      <p className="ad__sub">
        The till stays signed in. What changes is whose name is on everything from now on.
      </p>

      <div className="pin__dots" aria-live="polite" aria-label={`${pin.length} digits entered`}>
        {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
          <span key={i} className={`pin__dot${i < pin.length ? ' is-on' : ''}`} />
        ))}
      </div>

      {/* Whole digits, no decimal point: a PIN is not an amount. */}
      <NumberPad
        value={pin}
        decimals={0}
        label="PIN"
        onChange={(next) => setPin(next.replace(/\D/g, '').slice(0, PIN_MAX))}
      />

      <ActionError error={swap.error} action="Taking over the till" />

      <Button
        variant="primary"
        size="lg"
        block
        loading={swap.isPending}
        disabled={pin.length < 4}
        onClick={() => {
          // A PIN the server accepted also opens the lock with no connection.
          swap.mutateAsync({ pin }).then(
            (tokens) => void rememberPin(pin, tokens),
            () => undefined,
          );
        }}
      >
        Take over
      </Button>
    </Sheet>
  );
}
